import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import api from '../utils/api';
import { useAuth } from './AuthContext';

const WarehouseContext = createContext();

const STORAGE_KEY = 'activeWarehouseId';

export const WarehouseProvider = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const [warehouses, setWarehouses] = useState([]);
  const [activeWarehouseId, setActiveWarehouseIdState] = useState(
    () => localStorage.getItem(STORAGE_KEY) || null
  );

  const fetchWarehouses = useCallback(async () => {
    try {
      const res = await api.get('/warehouses');
      setWarehouses(res.data.data);
    } catch {
      // silently fail — auth not ready yet
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) fetchWarehouses();
    else setWarehouses([]);
  }, [isAuthenticated, fetchWarehouses]);

  const setActiveWarehouse = (id) => {
    if (id) localStorage.setItem(STORAGE_KEY, id);
    else localStorage.removeItem(STORAGE_KEY);
    setActiveWarehouseIdState(id);
  };

  const activeWarehouse = warehouses.find((w) => w._id === activeWarehouseId) || warehouses[0] || null;

  // admin & super_admin switch via the header; everyone else is locked to their warehouse
  const canSwitch = ['admin', 'super_admin'].includes(user?.role);

  // For admins with no explicit choice yet, default to (and persist) the first
  // warehouse so requests actually carry x-warehouse-id — otherwise the header
  // shows a warehouse that was never really selected and writes fail with
  // "No warehouse selected".
  useEffect(() => {
    if (canSwitch && warehouses.length > 0 && !activeWarehouseId) {
      setActiveWarehouse(warehouses[0]._id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canSwitch, warehouses, activeWarehouseId]);
  const effectiveWarehouseId = canSwitch
    ? (activeWarehouseId || null)
    : (user?.warehouse?._id ?? user?.warehouse ?? null);

  return (
    <WarehouseContext.Provider value={{
      warehouses,
      activeWarehouse,
      activeWarehouseId: effectiveWarehouseId,
      setActiveWarehouse,
      refetchWarehouses: fetchWarehouses,
    }}>
      {children}
    </WarehouseContext.Provider>
  );
};

export const useWarehouse = () => useContext(WarehouseContext);
